import { workflowHref, workflows } from '~/lib/workflows';

export type DiagramStage = {
  title: string;
  description: string;
  /** Outline and badge coordinates in the original reference artwork. */
  path: string;
  badge: [number, number, number];
  arrow?: { x: number; y: number; width: number; height: number };
};

export type UseCaseDiagram = {
  id: string;
  title: string;
  description: string;
  image: string;
  detailHref: string;
  width: number;
  height: number;
  cropTop: number;
  cropHeight: number;
  stages: DiagramStage[];
  outcome: string;
  /** The final scroll beat emphasizes these outcome pills in the artwork. */
  outcomeRegions: { x: number; y: number; width: number; height: number }[];
  returnPath?: string;
  returnCopy?: string;
  returnLabel?: { x: number; y: number; width: number; height: number };
};

export const accountDiagram: UseCaseDiagram = {
  id: 'account-prioritization-diagram',
  title: 'Continuous account prioritization',
  description:
    'Lumnis monitors your account tiers and delivers weekly priorities, with evidence and next steps.',
  image: '/assets/use-case-diagrams/account-prioritization-v2.png',
  detailHref: workflowHref(workflows[0]),
  width: 1774,
  height: 887,
  cropTop: 65,
  cropHeight: 790,
  outcome:
    'Fewer, better touches. Coverage across your account list. A record of signals and follow-ups.',
  outcomeRegions: [
    { x: 50, y: 773, width: 529, height: 67 },
    { x: 605, y: 773, width: 529, height: 67 },
    { x: 1162, y: 773, width: 562, height: 67 }
  ],
  stages: [
    {
      title: 'Your account tiers',
      description:
        'You set the tiers and criteria: Tier 1, AE-owned named accounts reviewed weekly; Tier 2, SDR-managed accounts reviewed weekly; Tier 3, programmatic coverage monthly.',
      path: 'M55 91 L375 132 Q396 135 399 163 L435 605 Q440 635 416 640 L53 687 Q30 691 30 668 L30 118 Q30 94 55 91 Z',
      badge: [51, 113, 32],
      arrow: { x: 399, y: 354, width: 65, height: 50 }
    },
    {
      title: 'Lumnis stacks signals',
      description:
        'Lumnis combines buying-group posts and engagement, hiring and role changes, company events and news, and CRM context such as stage, owner, and history. Weekly signals build on your CRM history.',
      path: 'M486 121 L802 130 Q824 131 827 158 L859 632 Q861 661 834 665 L490 671 Q466 673 465 648 L462 146 Q462 122 486 121 Z',
      badge: [474, 139, 31],
      arrow: { x: 837, y: 354, width: 56, height: 50 }
    },
    {
      title: "This week's priorities",
      description:
        'Illustrative weekly review: 2,600 accounts monitored, 180 with new signals, 12 worth reviewing, and 4 prioritized for outreach.',
      path: 'M900 130 L1133 132 Q1158 133 1159 157 L1150 644 Q1149 665 1126 667 L904 670 Q880 668 880 645 L878 154 Q878 132 900 130 Z',
      badge: [893, 144, 31],
      arrow: { x: 1154, y: 354, width: 49, height: 50 }
    },
    {
      title: 'Your account brief',
      description:
        'Brightvault, Tier 1. Why now: buyer engagement, a new security leader, and hiring point to a timely conversation. Evidence: Sep 8, two buyers engaged a competitor; Sep 5, a new security leader was announced; Sep 3, five platform roles were posted. Next move: send a tailored note about platform security hiring. Owner: assigned account executive.',
      path: 'M1226 102 H1729 Q1750 102 1750 124 V670 Q1750 692 1729 692 H1226 Q1203 692 1203 671 V124 Q1203 102 1226 102 Z',
      badge: [1226, 114, 33]
    }
  ]
};

export const audienceDiagram: UseCaseDiagram = {
  id: 'audience-content-loop-diagram',
  title: 'Compounding audience engine',
  description:
    'You define the ICP. Lumnis runs prospecting, connections, research, content, and follow-up.',
  image: '/assets/use-case-diagrams/audience-engine-v2.png',
  detailHref: workflowHref(workflows[1]),
  width: 1672,
  height: 941,
  cropTop: 108,
  cropHeight: 762,
  outcome:
    'An audience of relevant buyers. Buyer-informed content. Pipeline from engaged buyers. Engagement and replies improve the next cycle.',
  outcomeRegions: [
    { x: 45, y: 759, width: 532, height: 100 },
    { x: 594, y: 759, width: 484, height: 100 },
    { x: 1096, y: 759, width: 532, height: 100 }
  ],
  returnPath:
    'M1498 573 V582 Q1498 636 1445 636 H234 Q174 636 174 579 M162 586 L174 573 L186 586',
  returnCopy: 'Engagement and replies improve the next cycle.',
  returnLabel: { x: 653, y: 609, width: 365, height: 50 },
  stages: [
    {
      title: 'Find the right prospects',
      description:
        'Lumnis finds people at ICP-fit accounts and builds a fresh prospect list each week.',
      path: 'M54 146 H308 Q328 146 328 167 V546 Q328 566 308 566 H55 Q35 566 35 546 V166 Q35 146 54 146 Z',
      badge: [74, 155, 33],
      arrow: { x: 328, y: 318, width: 36, height: 43 }
    },
    {
      title: 'Grow your audience',
      description:
        'Lumnis sends weekly LinkedIn connection requests to those prospects.',
      path: 'M385 146 H638 Q658 146 658 167 V546 Q658 566 638 566 H385 Q365 566 365 546 V166 Q365 146 385 146 Z',
      badge: [390, 155, 33],
      arrow: { x: 658, y: 318, width: 34, height: 43 }
    },
    {
      title: 'Research buyer priorities',
      description:
        'Lumnis identifies the questions, pains, and topics your audience cares about.',
      path: 'M710 146 H961 Q982 146 982 167 V546 Q982 566 961 566 H710 Q690 566 690 546 V166 Q690 146 710 146 Z',
      badge: [717, 155, 33],
      arrow: { x: 982, y: 318, width: 34, height: 43 }
    },
    {
      title: 'Publish relevant content',
      description: 'Lumnis turns that research into daily or weekly posts.',
      path: 'M1035 146 H1287 Q1308 146 1308 167 V546 Q1308 566 1287 566 H1035 Q1014 566 1014 546 V167 Q1014 146 1035 146 Z',
      badge: [1041, 155, 33],
      arrow: { x: 1308, y: 318, width: 37, height: 43 }
    },
    {
      title: 'Follow up on engagement',
      description:
        'Lumnis identifies who engages and reaches out with relevant context.',
      path: 'M1366 146 H1618 Q1638 146 1638 167 V546 Q1638 566 1618 566 H1366 Q1346 566 1346 546 V167 Q1346 146 1366 146 Z',
      badge: [1373, 155, 33]
    }
  ]
};

/** Start with the first step; reserve the final beat for the full cycle and outcomes. */
export function getDiagramFocus(progress: number, count: number): number {
  if (count < 1) return -1;
  if (!Number.isFinite(progress) || progress <= 0) return 0;
  if (progress >= 0.88) return count;
  return Math.min(count - 1, Math.floor((progress / 0.88) * count));
}

/** Shared visual states; a negative focus keeps standalone diagrams fully visible. */
export function getDiagramStageState(
  focus: number,
  index: number,
  count: number
) {
  if (focus < 0 || focus >= count) return 'overview';
  if (index === focus) return 'current';
  return index < focus ? 'complete' : 'upcoming';
}

export const DIAGRAM_MOTION_QUERY =
  '(min-width: 1000px) and (min-height: 680px) and (prefers-reduced-motion: no-preference)';
